import {
  VersionedTransactionResponse,
  MessageCompiledInstruction,
  CompiledInstruction,
} from '@solana/web3.js';
import { Buffer } from 'buffer';

/**
 * Extracts raw instruction data (as Buffers) for a specific program ID
 * from both top-level and inner instructions in a Solana VersionedTransactionResponse.
 *
 * This function is used to isolate program-specific instruction payloads,
 * which may be necessary for parsing custom messages or contracts.
 *
 * @param tx - A transaction response returned by `getTransaction`
 * @param programId - The base58 address of the target program to extract instructions for
 * @returns An array of Buffers containing raw instruction data
 */
export function extractProgramInstructionData(
  tx: VersionedTransactionResponse,
  programId: string
): Buffer[] {
  // Fail fast if the transaction is null or malformed
  if (!tx?.transaction?.message) return [];

  // Get the deserialized message object and static account list
  const message = tx.transaction.message;
  const accountKeys = message.getAccountKeys().staticAccountKeys;

  /**
   * Helper: Resolves a programId string from a compiled instruction's programIdIndex.
   * Returns empty string if out of bounds.
   */
  const resolveProgramIdFromCompiled = (ix: { programIdIndex: number }): string =>
    accountKeys[ix.programIdIndex]?.toBase58() ?? '';

  /**
   * Extract matching top-level instructions.
   * These are the instructions directly on the transaction's message.
   * Note: The instruction `data` here is already a Buffer (not base64).
   */
  const topLevel = message.compiledInstructions
    .filter((ix: MessageCompiledInstruction) => resolveProgramIdFromCompiled(ix) === programId)
    .map((ix) => Buffer.from(ix.data));

  /**
   * Extract matching inner instructions (e.g., invoked via CPI).
   * These are often base64-encoded in RPC responses.
   * We cast to CompiledInstruction[] because innerInstructions are loosely typed.
   */
  const inner = (tx.meta?.innerInstructions ?? [])
    .flatMap((innerIx) =>
      (innerIx.instructions as CompiledInstruction[])
        .filter((ix) => resolveProgramIdFromCompiled(ix) === programId)
        .map((ix) => Buffer.from(ix.data, 'base64'))
    );

  // Combine and return all matching instruction data
  return [...topLevel, ...inner];
}